import type { Resource } from "i18next";

import englishLocale, { type EnglishTranslation } from "./locales/en";

import type { LocaleDefinition } from "./locale-definition";

type ApplicationLocale = LocaleDefinition<EnglishTranslation>;

/*
 * import: 'default' means each value is the
 * locale definition itself rather than:
 *
 * { default: localeDefinition }
 */
const localeModules = import.meta.glob("./locales/*.ts", {
  eager: true,
  import: "default",
}) as Record<string, ApplicationLocale>;

const discoveredLocales = Object.values(localeModules);

if (discoveredLocales.length === 0) {
  throw new Error("No locale files were discovered.");
}

const localeMap = new Map<string, ApplicationLocale>();

for (const locale of discoveredLocales) {
  const languageCode = locale.metadata.code;

  if (localeMap.has(languageCode)) {
    throw new Error(`Duplicate locale code: ${languageCode}`);
  }

  localeMap.set(languageCode, locale);
}

export const fallbackLanguage = englishLocale.metadata.code;

export const availableLocales = Array.from(localeMap.values()).sort(
  (first, second) =>
    first.metadata.displayName.localeCompare(second.metadata.displayName),
);

export const supportedLanguages = availableLocales.map(
  (locale) => locale.metadata.code,
);

/*
 * Required i18next resource shape:
 *
 * {
 *   en: {
 *     translation: {
 *       navigation: {},
 *       componentShowcase: {},
 *     },
 *   },
 * }
 */
export const translationResources: Resource = Object.fromEntries(
  availableLocales.map((locale) => [
    locale.metadata.code,
    {
      translation: locale.translation,
    },
  ]),
);

export function resolveLanguageCode(
  language: string | null | undefined,
): string {
  if (!language) {
    return fallbackLanguage;
  }

  const normalizedLanguage = language.trim().toLowerCase().split("-")[0];

  return localeMap.has(normalizedLanguage)
    ? normalizedLanguage
    : fallbackLanguage;
}

export function getLocaleMetadata(language: string | null | undefined) {
  const languageCode = resolveLanguageCode(language);

  return (localeMap.get(languageCode) ?? englishLocale).metadata;
}
